<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Save } from 'lucide-vue-next'
import { normalizeApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import {
  createProduct,
  getProduct,
  getProductUoms,
  listProductAudiences,
  listProductGroups,
  listProductSeasons,
  listUnitReferences,
  replaceProductUoms,
  updateProduct,
} from '../productService'
import {
  catalogTypeForKind,
  isManufacturedProduct,
} from '../productPresentation'
import type {
  CatalogKind,
  EngineeringProduct,
  ProductAudienceOption,
  ProductGroupOption,
  ProductPayload,
  ProductSeasonOption,
  ProductUom,
  UnitReference,
} from '../types'

interface FormErrors {
  name?: string
  sku?: string
  type?: string
  unit?: string
  audience_segment?: string
  season?: string
  min_order_quantity?: string
  socks_per_pack?: string
  packs_per_bag?: string
  group_id?: string
  tags?: string
  uoms?: string
}

const route = useRoute()
const router = useRouter()
const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()

const loading = ref(true)
const submitting = ref(false)
const loadError = ref('')
const submitError = ref('')
const groups = ref<ProductGroupOption[]>([])
const audiences = ref<ProductAudienceOption[]>([])
const seasons = ref<ProductSeasonOption[]>([])
const units = ref<UnitReference[]>([])
const formProduct = ref<EngineeringProduct | null>(null)
const errors = reactive<FormErrors>({})
const unitsLoading = ref(false)
const unitsError = ref('')
type EditableProductUom = {
  id: string
  uom_code: string
  label: string
  to_base_factor: number
  is_base: boolean
  is_fractional_allowed: boolean
  display_order: number
  is_active: boolean
}

const productId = computed(() => Number(route.params.id))
const isEditing = computed(() => Number.isInteger(productId.value) && productId.value > 0)
const canManage = computed(
  () => auth.user?.permissions.some((permission) => permission === 'products.create' || permission === 'products.update') ?? false,
)
const catalogKind = computed<CatalogKind>(() =>
  route.meta.catalogKind === 'raw-materials' ||
  route.meta.catalogKind === 'spare-parts'
    ? route.meta.catalogKind
    : 'manufactured',
)
const isManufacturedCatalog = computed(() => catalogKind.value === 'manufactured')
const unitOptions = computed(() => {
  const currentUnit = form.unit.trim()
  if (!currentUnit || units.value.some((unit) => unit.name === currentUnit)) {
    return units.value
  }

  return [
    {
      id: -1,
      type: 'unit',
      code: currentUnit.toUpperCase(),
      name: currentUnit,
      base_amount: null,
      is_active: true,
      sort_order: -1,
    } satisfies UnitReference,
    ...units.value,
  ]
})
const listPath = computed(() =>
  catalogKind.value === 'manufactured' ? '/products' : `/${catalogKind.value}`,
)
const detailPath = computed(() =>
  isEditing.value
    ? (catalogKind.value === 'manufactured'
      ? `/products/${productId.value}`
      : `/${catalogKind.value}/${productId.value}`)
    : listPath.value,
)
const catalogType = computed(() =>
  catalogKind.value === 'manufactured'
    ? form.type
    : (catalogTypeForKind(catalogKind.value) as ProductPayload['type'] | undefined) ?? form.type,
)
const pageTitleKey = computed(() =>
  catalogKind.value === 'raw-materials'
    ? 'raw'
    : catalogKind.value === 'spare-parts'
      ? 'spare'
      : 'manufactured',
)
const isUomsLoading = ref(false)
const isUomsSubmitting = ref(false)
const uomsError = ref('')
const uomsValidationError = ref('')
const uomsSuccess = ref(false)
const uomOptions = computed(() => {
  const options = units.value
    .filter((unit) => unit.is_active !== 0 && unit.is_active !== false)
    .map((unit) => ({
      value: String(unit.code || unit.name).trim().toUpperCase(),
      label: unit.code && unit.name && unit.code !== unit.name
        ? `${unit.name} (${unit.code})`
        : unit.name || unit.code,
    }))
    .filter((option) => option.value)

  uomRows.value.forEach((row) => {
    const code = String(row.uom_code || '').trim().toUpperCase()
    if (code && !options.some((option) => option.value === code)) {
      options.unshift({ value: code, label: code })
    }
  })

  return options
})
const productBaseUomName = computed(() => {
  const currentUnit = form.unit.trim()
  if (!currentUnit) return ''
  const matching = unitOptions.value.find((unit) => unit.name === currentUnit)
  return matching ? `${matching.name} (${matching.code})` : currentUnit
})
const baseUomName = computed(() => {
  const baseRow = uomRows.value.find((row) => row.is_base)
  return baseRow ? uomRowName(baseRow) : ''
})

const form = reactive({
  name: '',
  sku: '',
  internal_sku_code: '',
  type: 'manufactured_good' as ProductPayload['type'],
  audience_id: '',
  season_id: '',
  unit: 'pair',
  ean13: '',
  min_order_quantity: '',
  socks_per_pack: '',
  packs_per_bag: '',
  group_id: '',
  tags: '',
})

let nextUomId = 0

function createEditableUom(overrides: Partial<EditableProductUom> = {}): EditableProductUom {
  nextUomId += 1
  return {
    id: `uom-${nextUomId}`,
    uom_code: '',
    label: '',
    to_base_factor: 1,
    is_base: false,
    is_fractional_allowed: false,
    display_order: nextUomId,
    is_active: true,
    ...overrides,
  }
}

const uomRows = ref<EditableProductUom[]>([])

function clearErrors(): void {
  for (const key of Object.keys(errors) as Array<keyof FormErrors>) {
    delete errors[key]
  }
  submitError.value = ''
}

function assignForm(product: EngineeringProduct): void {
  form.name = product.name
  form.sku = product.sku
  form.internal_sku_code = product.internal_sku_code ?? ''
  form.type = isManufacturedProduct(product)
    ? product.type as ProductPayload['type']
    : 'manufactured_good'
  form.audience_id = product.audience_id != null ? String(product.audience_id) : ''
  form.season_id = product.season_id != null ? String(product.season_id) : ''
  form.unit = product.unit
  form.ean13 = product.ean13 ?? ''
  form.min_order_quantity = product.min_order_quantity != null ? String(product.min_order_quantity) : ''
  form.socks_per_pack = product.socks_per_pack != null ? String(product.socks_per_pack) : ''
  form.packs_per_bag = product.packs_per_bag != null ? String(product.packs_per_bag) : ''
  form.group_id = product.group_id != null ? String(product.group_id) : ''
  form.tags = product.tags.join(', ')
}

function assignUoms(uoms: ProductUom[]): void {
  uomRows.value = uoms
    .slice()
    .sort((left, right) => (left.display_order ?? 0) - (right.display_order ?? 0))
    .map((uom) => createEditableUom({
      uom_code: uom.uom_code ?? '',
      label: uom.label ?? '',
      to_base_factor: Number(uom.to_base_factor || 1),
      is_base: Boolean(uom.is_base),
      is_fractional_allowed: Boolean(uom.is_fractional_allowed),
      display_order: Number(uom.display_order || 1),
      is_active: uom.is_active == null ? true : Boolean(uom.is_active),
    }))
}

function positiveInteger(value: string): boolean {
  return Number.isInteger(Number(value)) && Number(value) > 0
}

function validate(): boolean {
  clearErrors()

  if (!form.name.trim()) errors.name = t('engineering.productForm.validation.name')
  if (!form.sku.trim()) errors.sku = t('engineering.productForm.validation.sku')
  if (!form.type) errors.type = t('engineering.productForm.validation.type')
  if (!form.unit.trim()) errors.unit = t('engineering.productForm.validation.unit')
  if (form.min_order_quantity && !positiveInteger(form.min_order_quantity)) errors.min_order_quantity = t('engineering.productForm.validation.minOrder')
  if (form.socks_per_pack && !positiveInteger(form.socks_per_pack)) errors.socks_per_pack = t('engineering.productForm.validation.socksPerPack')
  if (form.packs_per_bag && !positiveInteger(form.packs_per_bag)) errors.packs_per_bag = t('engineering.productForm.validation.packsPerBag')

  return Object.keys(errors).length === 0
}

function buildPayload(): ProductPayload {
  return {
    name: form.name.trim(),
    sku: form.sku.trim(),
    internal_sku_code: form.internal_sku_code.trim() || undefined,
    type: catalogType.value,
    audience_id: isManufacturedCatalog.value && form.audience_id ? Number(form.audience_id) : undefined,
    season_id: isManufacturedCatalog.value && form.season_id ? Number(form.season_id) : undefined,
    unit: form.unit.trim(),
    ean13: form.ean13.trim() || undefined,
    min_order_quantity: form.min_order_quantity ? Number(form.min_order_quantity) : undefined,
    socks_per_pack: form.socks_per_pack ? Number(form.socks_per_pack) : undefined,
    packs_per_bag: form.packs_per_bag ? Number(form.packs_per_bag) : undefined,
    group_id: form.group_id ? Number(form.group_id) : undefined,
    tags: form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
  }
}

function buildUomPayload(): ProductUom[] {
  return uomRows.value.map((row, index) => ({
    uom_code: row.uom_code.trim().toUpperCase(),
    label: row.label.trim(),
    to_base_factor: Number(row.to_base_factor),
    is_base: row.is_base ? 1 : 0,
    is_fractional_allowed: row.is_fractional_allowed ? 1 : 0,
    display_order: Number(row.display_order || index + 1),
    is_active: row.is_active ? 1 : 0,
  }))
}

function addUomRow(): void {
  uomRows.value = [
    ...uomRows.value,
    createEditableUom({
      is_base: uomRows.value.length === 0,
      to_base_factor: 1,
      label: uomRows.value.length === 0 ? form.unit.trim() || '' : '',
      uom_code: uomRows.value.length === 0 ? (form.unit.trim() || '').toUpperCase() : '',
      display_order: uomRows.value.length + 1,
    }),
  ]
  uomsSuccess.value = false
}

function removeUomRow(id: string): void {
  if (uomRows.value.length <= 1) return
  uomRows.value = uomRows.value.filter((row) => row.id !== id)
  uomsSuccess.value = false
}

function setBaseUom(id: string): void {
  uomRows.value = uomRows.value.map((row) => ({
    ...row,
    is_base: row.id === id,
    to_base_factor: row.id === id && !(Number(row.to_base_factor) > 0) ? 1 : row.to_base_factor,
  }))
  uomsSuccess.value = false
}

function handleUomCodeSelect(row: EditableProductUom): void {
  const code = String(row.uom_code || '').trim().toUpperCase()
  row.uom_code = code
  const matching = units.value.find((unit) => String(unit.code || '').trim().toUpperCase() === code)
  if (matching) {
    row.label = String(matching.name || '').trim()
  }
  if (!String(row.label || '').trim()) {
    row.label = code
  }
}

function uomRowName(row: EditableProductUom): string {
  const label = String(row.label || '').trim()
  const code = String(row.uom_code || '').trim()
  if (label && code) return `${label} (${code})`
  return label || code || t('engineering.productForm.selectUnit')
}

function validateUoms(): string {
  if (!uomRows.value.length) {
    return t('engineering.productForm.validation.uomBase')
  }

  const seenCodes = new Set<string>()
  let baseCount = 0

  for (const row of uomRows.value) {
    const code = String(row.uom_code || '').trim().toUpperCase()
    if (!code) {
      return t('engineering.productForm.validation.uomCodeRequired')
    }
    if (seenCodes.has(code)) {
      return t('engineering.productForm.validation.uomUnique')
    }
    seenCodes.add(code)

    if (!String(row.label || '').trim()) {
      return t('engineering.productForm.validation.uomLabelRequired')
    }
    if (!(Number(row.to_base_factor) > 0)) {
      return t('engineering.productForm.validation.uomFactor')
    }
    if (row.is_base) {
      baseCount += 1
    }
  }

  if (baseCount !== 1) {
    return t('engineering.productForm.validation.uomBase')
  }

  return ''
}

async function loadProductUoms(): Promise<void> {
  if (!isEditing.value) {
    uomRows.value = []
    return
  }

  isUomsLoading.value = true
  uomsError.value = ''
  uomsValidationError.value = ''
  try {
    const uoms = await getProductUoms(client, productId.value)
    assignUoms(uoms)
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    uomsError.value = normalized.message
  } finally {
    isUomsLoading.value = false
  }
}

async function submitUoms(): Promise<void> {
  uomsValidationError.value = validateUoms()
  if (uomsValidationError.value) return

  isUomsSubmitting.value = true
  uomsError.value = ''
  uomsSuccess.value = false

  try {
    const saved = await replaceProductUoms(client, productId.value, buildUomPayload())
    assignUoms(saved)
    uomsSuccess.value = true
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    uomsError.value = normalized.message
  } finally {
    isUomsSubmitting.value = false
  }
}

async function loadDependencies(): Promise<void> {
  loading.value = true
  loadError.value = ''

  try {
    const [groupList, audienceList, seasonList, product] = await Promise.all([
      listProductGroups(client),
      listProductAudiences(client),
      listProductSeasons(client),
      isEditing.value ? getProduct(client, productId.value) : Promise.resolve(null),
    ])
    groups.value = groupList
    audiences.value = audienceList
    seasons.value = seasonList

    if (product) {
      const matchesSection = catalogKind.value === 'manufactured'
        ? isManufacturedProduct(product)
        : product.type === catalogTypeForKind(catalogKind.value)
      if (!matchesSection) {
        throw new Error(t('engineering.productForm.invalidType'))
      }
      formProduct.value = product
      assignForm(product)
    } else if (!isManufacturedCatalog.value) {
      form.type = 'manufactured_good'
      assignUoms([])
    }
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    loadError.value = normalized.message
    return
  } finally {
    loading.value = false
  }

  await reloadUnits()
  await loadProductUoms()
}

async function reloadUnits(): Promise<void> {
  unitsLoading.value = true
  unitsError.value = ''

  try {
    units.value = await listUnitReferences(client)
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    unitsError.value = normalized.message
  } finally {
    unitsLoading.value = false
  }
}

async function submit(): Promise<void> {
  if (!validate()) return

  submitting.value = true
  try {
    const payload = buildPayload()
    const product = isEditing.value
      ? await updateProduct(client, productId.value, payload)
      : await createProduct(client, payload)
    const targetPath = catalogKind.value === 'manufactured'
      ? `/products/${product.id}`
      : `/${catalogKind.value}/${product.id}`
    await router.push(targetPath)
  } catch (error: unknown) {
    const normalized = normalizeApiError(error)
    submitError.value = normalized.correlationId
      ? `${normalized.message} Reference: ${normalized.correlationId}`
      : normalized.message

    if (normalized.errors) {
      for (const [field, messages] of Object.entries(normalized.errors)) {
        const key = field as keyof FormErrors
        if (messages[0] && key in errors) {
          errors[key] = messages[0]
        }
      }
    }
  } finally {
    submitting.value = false
  }
}

onMounted(loadDependencies)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :to="isEditing ? detailPath : listPath">
      {{ isEditing ? $t('engineering.productForm.backItem') : $t('engineering.productForm.backList') }}
    </RouterLink>
    <div v-if="canManage" class="page-toolbar__actions">
      <button class="page-toolbar__action--primary" type="submit" form="product-form" :disabled="loading || submitting">
        <Save v-if="!submitting" :size="16" aria-hidden="true" />
        {{ submitting ? $t('engineering.productForm.saving') : isEditing ? $t('engineering.productForm.save') : $t('engineering.productForm.create') }}
      </button>
    </div>
  </div>

  <div class="page-heading">
    <p class="eyebrow">{{ $t('engineering.productForm.eyebrow') }}</p>
    <h2>{{ isEditing ? $t(`engineering.productForm.editTitle.${pageTitleKey}`) : $t(`engineering.productForm.createTitle.${pageTitleKey}`) }}</h2>
    <p>
      {{ isEditing
        ? $t(`engineering.productForm.editDescription.${pageTitleKey}`)
        : $t(`engineering.productForm.createDescription.${pageTitleKey}`) }}
    </p>
  </div>

  <div v-if="loading" class="form-state">{{ $t('engineering.productForm.loading') }}</div>
  <div v-else-if="loadError" class="form-state form-state--error">
    <p role="alert">{{ loadError }}</p>
    <button type="button" @click="loadDependencies">{{ $t('engineering.action.retry') }}</button>
  </div>

  <form v-else id="product-form" @submit.prevent="submit">
    <div v-if="submitError" class="error-banner" role="alert">{{ submitError }}</div>

    <section>
      <h3>{{ $t('engineering.productForm.identity') }}</h3>
      <div class="form-grid">
        <label>
          <span>{{ $t('engineering.productForm.name') }} *</span>
          <input v-model="form.name" type="text" :aria-invalid="Boolean(errors.name)">
          <small v-if="errors.name">{{ errors.name }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.sku') }} *</span>
          <input v-model="form.sku" type="text" :aria-invalid="Boolean(errors.sku)">
          <small v-if="errors.sku">{{ errors.sku }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.internalSku') }}</span>
          <input v-model="form.internal_sku_code" type="text">
        </label>
        <label>
          <span>{{ $t('engineering.productForm.barcode') }}</span>
          <input v-model="form.ean13" type="text" inputmode="numeric">
        </label>
      </div>
    </section>

    <section>
      <h3>{{ $t('engineering.productForm.classification') }}</h3>
      <div class="form-grid">
        <label v-if="isManufacturedCatalog">
          <span>{{ $t('engineering.productForm.type') }} *</span>
          <select v-model="form.type" :aria-invalid="Boolean(errors.type)">
            <option value="manufactured_good">{{ $t('engineering.catalog.manufacturedGood') }}</option>
            <option value="finished_good">{{ $t('engineering.catalog.finishedGood') }}</option>
            <option value="semi_finished_good">{{ $t('engineering.catalog.semiFinishedGood') }}</option>
            <option value="semi_finished">{{ $t('engineering.catalog.semiFinished') }}</option>
          </select>
          <small v-if="errors.type">{{ errors.type }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.group') }}</span>
          <select v-model="form.group_id" :aria-invalid="Boolean(errors.group_id)">
            <option value="">{{ $t('engineering.productForm.selectGroup') }}</option>
            <option v-for="group in groups" :key="group.id" :value="String(group.id)">
              {{ group.name }} ({{ group.code }})
            </option>
          </select>
          <small v-if="errors.group_id">{{ errors.group_id }}</small>
        </label>
        <label v-if="isManufacturedCatalog">
          <span>{{ $t('engineering.productForm.audience') }}</span>
          <select v-model="form.audience_id" :aria-invalid="Boolean(errors.audience_segment)">
            <option value="">{{ $t('engineering.product.notSet') }}</option>
            <option v-for="audience in audiences" :key="audience.id" :value="String(audience.id)">
              {{ audience.name }}
            </option>
          </select>
          <small v-if="errors.audience_segment">{{ errors.audience_segment }}</small>
        </label>
        <label v-if="isManufacturedCatalog">
          <span>{{ $t('engineering.productForm.season') }}</span>
          <select v-model="form.season_id" :aria-invalid="Boolean(errors.season)">
            <option value="">{{ $t('engineering.product.notSet') }}</option>
            <option v-for="seasonOption in seasons" :key="seasonOption.id" :value="String(seasonOption.id)">
              {{ seasonOption.name }}
            </option>
          </select>
          <small v-if="errors.season">{{ errors.season }}</small>
        </label>
      </div>
    </section>

    <section>
      <h3>{{ $t('engineering.productForm.packaging') }}</h3>
      <div class="form-grid" :class="{ 'form-grid--compact': !isManufacturedCatalog }">
        <label>
          <span>{{ $t('engineering.productForm.unit') }} *</span>
          <select v-model="form.unit" :aria-invalid="Boolean(errors.unit)" :disabled="unitsLoading || !unitOptions.length">
            <option value="">{{ unitsLoading ? $t('engineering.productForm.unitsLoading') : $t('engineering.productForm.selectUnit') }}</option>
            <option v-for="unit in unitOptions" :key="unit.id" :value="unit.name">
              {{ unit.name }} ({{ unit.code }})
            </option>
          </select>
          <small v-if="errors.unit">{{ errors.unit }}</small>
          <small v-else-if="unitsError">
            {{ unitsError }}
            <button class="inline-retry" type="button" @click="reloadUnits">{{ $t('engineering.action.retry') }}</button>
          </small>
          <small v-else>{{ $t('engineering.productForm.unitHelp') }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.minOrder') }}</span>
          <input v-model="form.min_order_quantity" type="number" min="1" step="1" :aria-invalid="Boolean(errors.min_order_quantity)">
          <small v-if="errors.min_order_quantity">{{ errors.min_order_quantity }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.socksPerPack') }}</span>
          <input v-model="form.socks_per_pack" type="number" min="1" step="1" :aria-invalid="Boolean(errors.socks_per_pack)">
          <small v-if="errors.socks_per_pack">{{ errors.socks_per_pack }}</small>
        </label>
        <label>
          <span>{{ $t('engineering.productForm.packsPerBag') }}</span>
          <input v-model="form.packs_per_bag" type="number" min="1" step="1" :aria-invalid="Boolean(errors.packs_per_bag)">
          <small v-if="errors.packs_per_bag">{{ errors.packs_per_bag }}</small>
        </label>
      </div>
    </section>

    <section>
      <div class="section-header">
        <div>
          <h3>{{ $t('engineering.productForm.uoms') }}</h3>
          <p>{{ $t('engineering.productForm.uomsHelp') }}</p>
          <p v-if="baseUomName" class="uom-base-name">
            {{ $t('engineering.productForm.uomBaseSummary', { unit: baseUomName || productBaseUomName }) }}
          </p>
        </div>
        <button class="secondary-button" type="button" :disabled="!isEditing || isUomsSubmitting" @click="addUomRow">
          {{ $t('engineering.productForm.addUom') }}
        </button>
      </div>
      <p v-if="!isEditing" class="uom-empty">{{ $t('engineering.productForm.uomsSaveFirst') }}</p>
      <p v-else-if="isUomsLoading" class="uom-empty">{{ $t('engineering.productForm.uomsLoading') }}</p>
      <p v-else-if="uomsError" class="section-error" role="alert">{{ uomsError }}</p>
      <div v-else-if="uomRows.length" class="uom-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('engineering.productForm.uomCode') }}</th>
              <th>{{ $t('engineering.productForm.uomLabel') }}</th>
              <th>{{ $t('engineering.productForm.uomFactor') }}</th>
              <th>{{ $t('engineering.productForm.uomBase') }}</th>
              <th>{{ $t('engineering.productForm.uomFractional') }}</th>
              <th>{{ $t('engineering.productForm.uomStatus') }}</th>
              <th>{{ $t('engineering.productForm.uomActions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in uomRows" :key="row.id">
              <td>
                <select v-model="row.uom_code" :disabled="isUomsSubmitting" @change="handleUomCodeSelect(row)">
                  <option value="">{{ $t('engineering.productForm.selectUnit') }}</option>
                  <option v-for="option in uomOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </td>
              <td><input v-model="row.label" type="text"></td>
              <td><input v-model.number="row.to_base_factor" type="number" min="0.0001" step="0.0001" :disabled="isUomsSubmitting"></td>
              <td class="uom-cell-center">
                <input :checked="row.is_base" type="radio" name="base-uom" :disabled="isUomsSubmitting" @change="setBaseUom(row.id)">
              </td>
              <td class="uom-cell-center">
                <input v-model="row.is_fractional_allowed" type="checkbox" :disabled="isUomsSubmitting">
              </td>
              <td class="uom-cell-center">
                <input v-model="row.is_active" type="checkbox" :disabled="isUomsSubmitting">
              </td>
              <td class="uom-cell-center">
                <button class="inline-retry" type="button" :disabled="isUomsSubmitting || uomRows.length <= 1" @click="removeUomRow(row.id)">
                  {{ $t('engineering.productForm.removeUom') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="uom-actions">
          <p v-if="uomsValidationError" class="section-error" role="alert">{{ uomsValidationError }}</p>
          <p v-if="uomsSuccess" class="uom-success">{{ $t('engineering.productForm.uomsSaved') }}</p>
          <button class="page-toolbar__action--primary" type="button" :disabled="isUomsSubmitting" @click="submitUoms">
            {{ isUomsSubmitting ? $t('engineering.productForm.uomsSaving') : $t('engineering.productForm.uomsSave') }}
          </button>
        </div>
      </div>
      <p v-else class="uom-empty">{{ $t('engineering.productForm.uomsEmpty') }}</p>
    </section>

    <section>
      <h3>{{ $t('engineering.productForm.tagsSection') }}</h3>
      <label>
        <span>{{ $t('engineering.productForm.tags') }}</span>
        <input v-model="form.tags" type="text" :placeholder="$t('engineering.productForm.tagsPlaceholder')" :aria-invalid="Boolean(errors.tags)">
        <small v-if="errors.tags">{{ errors.tags }}</small>
        <small v-else>{{ $t('engineering.productForm.tagsHelp') }}</small>
      </label>
    </section>
  </form>
</template>

<style scoped>
.back-link{display:inline-block;margin-bottom:20px;color:#285b9a;font-size:13px;font-weight:700}.page-heading{margin-bottom:24px}.eyebrow{margin:0 0 5px;color:#65736e;font-size:12px;font-weight:700;text-transform:uppercase}h2{margin:0;font-size:26px;letter-spacing:0}.page-heading>p:last-child{margin:7px 0 0;color:#64716c}section{margin-top:16px;padding:22px;border:1px solid #d7dfdc;background:#ffffff}section h3{margin:0 0 18px;font-size:16px;letter-spacing:0}.form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px}.form-grid--compact label:last-child:nth-child(4){grid-column:2}.form-grid--compact label:last-child:nth-child(2){grid-column:auto}label>span{display:block;margin-bottom:6px;color:#4e5d57;font-size:12px;font-weight:700}input,select{width:100%;min-height:42px;padding:9px 10px;border:1px solid #adb9b4;border-radius:4px;color:#17201d;background:#ffffff}input[aria-invalid="true"],select[aria-invalid="true"]{border-color:#b44839}label small{display:block;margin-top:5px;color:#697671;font-size:12px}label [aria-invalid="true"]+small{color:#9b3427}.error-banner,.form-state--error{border:1px solid #d7a8a1;color:#852d22;background:#fff5f3}.error-banner{padding:12px 14px}.form-state{padding:32px;border:1px dashed #c7d0cc;color:#65726d;background:#ffffff;text-align:center}.form-state button{min-height:38px;padding:7px 12px;border:1px solid #87958f;border-radius:4px;background:#ffffff;cursor:pointer}@media(min-width:1400px){.form-grid{grid-template-columns:repeat(4,minmax(0,1fr))}.form-grid--compact label:last-child:nth-child(4){grid-column:auto}}@media(max-width:700px){.form-grid{grid-template-columns:1fr}.form-grid--compact label:last-child:nth-child(4){grid-column:auto}section{padding:18px 14px}}
.section-header{display:flex;gap:16px;align-items:flex-start;justify-content:space-between}.section-header p{margin:4px 0 0;color:#697671;font-size:13px}.secondary-button{min-height:38px;padding:7px 12px;border:1px solid #87958f;border-radius:4px;color:#24403a;background:#ffffff;cursor:pointer}.section-error{margin:12px 0 0;color:#9b3427;font-size:13px}.uom-table{margin-top:16px;overflow-x:auto}.uom-table table{width:100%;border-collapse:collapse}.uom-table th,.uom-table td{padding:10px;border:1px solid #e1e6e4;text-align:left}.uom-table th{color:#586660;background:#f3f6f5;font-size:11px;text-transform:uppercase}.uom-table td input[type="radio"],.uom-table td input[type="checkbox"]{width:auto;min-height:auto;padding:0}.uom-cell-center{text-align:center}.uom-empty{margin:16px 0 0;color:#697671;font-size:13px}.inline-retry{padding:0;border:0;color:#285b9a;background:transparent;cursor:pointer;font:inherit}
</style>
